'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { ArrowLeft, ArrowRight, Pencil, Trash2, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useTeamBioStore, TeamBio } from '@/store/teamBioStore';
import { getStrapiImageUrl } from '@/lib/image-utils';
import { CreateTeamBioModal, GreenCTASection, Modal } from '@/components/ui';
import { aboutPageTranslations } from './translations';

type TabType = 'about' | 'partners' | 'team';

function TeamBioCard({
  member,
  readBioLabel,
  isAdmin,
  onView,
  onEdit,
  onDelete,
}: {
  member: TeamBio;
  readBioLabel: string;
  isAdmin: boolean;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const photoUrl = member.photo?.url ? getStrapiImageUrl(member.photo.url) : null;
  return (
    <div className="relative border border-border rounded-xl overflow-hidden cursor-pointer" onClick={onView}>
      <div className="h-62 bg-background-secondary">
        {photoUrl ? (
          <img src={photoUrl} alt={member.name} className="w-full h-full object-cover" />
        ) : null}
      </div>
      <div className="p-4 space-y-0.5">
        <p className="font-semibold text-primary text-base">{member.name}</p>
        <p className="text-sm text-foreground-muted">{member.title}</p>
        <p className="text-sm text-primary font-medium pt-1">{readBioLabel} →</p>
      </div>
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1.5 rounded-md bg-background-secondary hover:bg-sidebar-hover transition"
            title="Edit member"
          >
            <Pencil size={14} className="text-foreground-muted" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-md bg-background-secondary hover:bg-red-50 transition"
            title="Delete member"
          >
            <Trash2 size={14} className="text-red-500" />
          </button>
        </div>
      )}
    </div>
  );
}

const whatWeDoImages = [
  '/BringPartners1.png',
  '/UseData2.png',
  '/DirectResources3.png',
  '/MakeSupport4.png',
  '/Lead5.png',
];

const whatWeBelieveImages = [
  '/Box1.png',
  '/Box2.png',
  '/Box3.png',
  '/Box4.png',
];

export default function AboutPage() {
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { teamBios, fetchTeamBios, deleteTeamBio } = useTeamBioStore();
  const searchParams = useSearchParams();
  const initialTab = (['about', 'partners', 'team'] as TabType[]).includes(searchParams.get('tab') as TabType)
    ? (searchParams.get('tab') as TabType)
    : 'about';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [viewBio, setViewBio] = useState<TeamBio | null>(null);
  const [editBio, setEditBio] = useState<TeamBio | null>(null);
  const [showTeamBioModal, setShowTeamBioModal] = useState(false);
  const [deleteBio, setDeleteBio] = useState<TeamBio | null>(null);
  const [partnersTiers, setPartnersTiers] = useState<{ strategic: string | null; aligned: string | null; awareness: string | null } | null>(null);

  const isAdmin = currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin';

  useEffect(() => {
    if (activeTab === 'team') fetchTeamBios();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'partners' || partnersTiers !== null) return;
    let cancelled = false;
    fetch('/api/strapi/partners-section?populate=*')
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json?.data) return;
        const strategicUrl = json.data.strategicImage?.url
          ? getStrapiImageUrl(json.data.strategicImage.url)
          : null;
        const alignedUrl = json.data.alignedImage?.url
          ? getStrapiImageUrl(json.data.alignedImage.url)
          : null;
        const awarenessUrl = json.data.awarenessImage?.url
          ? getStrapiImageUrl(json.data.awarenessImage.url)
          : null;
        setPartnersTiers({ strategic: strategicUrl, aligned: alignedUrl, awareness: awarenessUrl });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeTab, partnersTiers]);

  const at = aboutPageTranslations[language];

  const staffMembers = teamBios.filter((b) => b.team === 'staff');
  const boardMembers = teamBios.filter((b) => b.team === 'board');

  const handleDeleteMember = async () => {
    if (!deleteBio) return;
    await deleteTeamBio((deleteBio as TeamBio).documentId);
    setDeleteBio(null);
    fetchTeamBios();
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'about', label: t.about.title },
    { id: 'partners', label: t.about.ourPartners },
    { id: 'team', label: t.about.ourTeam },
  ];

  return (
    <div className="p-3 md:p-6 space-y-6">
      <h1 className="text-[48px] leading-tight text-primary" style={{ fontFamily: 'var(--font-abhaya-libre)' }}>{t.nav.about}</h1>

      {/* Tabs */}
      <div className="flex gap-2 py-3 border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition whitespace-nowrap border ${
              activeTab === tab.id
                ? 'bg-tab-active-bg border-filter-bg text-black'
                : 'bg-white border-border text-foreground-muted hover:border-primary hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* About TogetherWe Tab */}
      {activeTab === 'about' && (
        <div className="mt-6 space-y-4">
          <div className="bg-white border border-border rounded-xl p-4 md:p-20 space-y-10">
            {/* Section 1: Who We Are */}
            <div className="bg-background-content px-4 py-6 md:px-20 md:py-10 rounded-xl flex flex-col sm:flex-row items-center gap-8">
              <div className="shrink-0 w-80">
                <Image
                  src="/1about.png"
                  alt={at.whoWeAre.title}
                  width={834}
                  height={768}
                  className="w-full h-auto"
                />
              </div>
              <div className="space-y-3">
                <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{at.whoWeAre.title}</h2>
                <p className="text-lg text-primary leading-relaxed">{at.whoWeAre.body}</p>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Section 2: Intro + View Our Work */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1 space-y-4">
                <p className="text-lg text-primary leading-relaxed">{at.intro.p1}</p>
                <p className="text-lg text-primary leading-relaxed">{at.intro.p2}</p>
                <Link
                  href="/our-work"
                  className="inline-block mt-2 px-8 py-2.5 border border-primary rounded-full text-sm font-semibold tracking-widest uppercase text-primary hover:bg-primary hover:text-white transition"
                >
                  {at.intro.viewOurWork}
                </Link>
              </div>
              <div className="shrink-0 w-full lg:w-72 h-56 rounded-xl overflow-hidden">
                <Image
                  src="/2about.jpg"
                  alt="Community gathering"
                  width={288}
                  height={224}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Section 3: What We Do list */}
            <div className="bg-background-content px-4 py-6 md:px-20 md:py-10 rounded-xl space-y-6">
              <div className="space-y-2">
                <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{at.whatWeDo.title}</h2>
                <p className="text-lg text-primary leading-relaxed">{at.whatWeDo.subtitle}</p>
              </div>
              <div className="space-y-6">
                {at.whatWeDo.items.map((item, i) => (
                    <div key={item.title} className="flex items-start gap-5">
                      <div className="w-24 h-24 rounded-full overflow-hidden shrink-0">
                        <Image
                          src={whatWeDoImages[i]}
                          alt={item.title}
                          width={96}
                          height={96}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold text-primary">{item.title}</h3>
                        <p className="text-base text-primary leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Section 4: Why This Work Matters */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="shrink-0 w-full lg:w-72 h-56 rounded-xl overflow-hidden">
                <Image
                  src="/3about.jpg"
                  alt={at.whyItMatters.title}
                  width={288}
                  height={224}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 space-y-4">
                <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{at.whyItMatters.title}</h2>
                <p className="text-lg text-primary leading-relaxed">{at.whyItMatters.p1}</p>
                <p className="text-lg text-primary leading-relaxed">{at.whyItMatters.p2}</p>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Section 5: TogetherWe exists to do what no single org can do alone */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1 space-y-4">
                <p className="text-lg text-primary leading-relaxed font-semibold">{at.systemSection.lead}</p>
                <ul className="space-y-1">
                  {at.systemSection.bullets.map((bullet) => (
                    <li key={bullet} className="text-lg text-primary leading-relaxed">
                      {bullet}
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-primary leading-relaxed">{at.systemSection.closing}</p>
              </div>
              <div className="shrink-0 w-full lg:w-72 h-56 rounded-xl overflow-hidden">
                <Image
                  src="/4about.jpg"
                  alt="Shared leadership and commitment"
                  width={288}
                  height={224}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* What We Believe */}
            <div className="bg-primary -mx-4 px-4 md:-mx-20 md:px-20 py-14 space-y-10">
              <h2 className="text-3xl font-semibold text-white font-[family-name:var(--font-abhaya-libre)]">{at.whatWeBelieve.title}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {at.whatWeBelieve.items.map((text, i) => (
                    <div key={text} className="bg-white/10 rounded-xl p-6 flex flex-col gap-6">
                      <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto">
                        <Image
                          src={whatWeBelieveImages[i]}
                          alt=""
                          width={80}
                          height={80}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-base text-white leading-relaxed">{text}</p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Our Origins */}
            <div className="bg-background-content px-4 py-6 md:px-20 md:py-10 rounded-xl space-y-6">
              <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{at.ourOrigins.title}</h2>
              <div className="space-y-4">
                {at.ourOrigins.paragraphs.map((p) => (
                  <p key={p} className="text-lg text-primary leading-relaxed">{p}</p>
                ))}
              </div>
            </div>

            {/* Tab navigation */}
            <div>
              <div className="border-t border-border mb-8" />
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setActiveTab('team')}
                  className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-primary hover:opacity-70 transition"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {at.nav.ourTeam}
                </button>
                <button
                  onClick={() => setActiveTab('partners')}
                  className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-primary hover:opacity-70 transition"
                >
                  {at.nav.ourPartners}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Our Partners Tab */}
      {activeTab === 'partners' && (
        <div className="mt-6 space-y-4">
          <div className="bg-white border border-border rounded-xl p-4 md:p-20 space-y-10">
            {/* Header */}
            <div className="space-y-4">
              <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{at.partners.title}</h2>
              <p className="text-lg text-primary leading-relaxed">{at.partners.p1}</p>
              <p className="text-lg text-primary leading-relaxed">{at.partners.p2}</p>
            </div>

            {/* Partner tiers — one composite image per tier; tiers with no image are hidden */}
            <div className="bg-background-content px-4 py-6 md:px-20 md:py-10 rounded-xl space-y-10">
              <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{at.partners.ourPartners}</h2>

              {([
                { key: 'strategic', label: at.partners.strategicLabel, url: partnersTiers?.strategic },
                { key: 'aligned',   label: at.partners.alignedLabel,   url: partnersTiers?.aligned },
                { key: 'awareness', label: at.partners.awarenessLabel, url: partnersTiers?.awareness },
              ] as const).filter((t) => !!t.url).map((t) => (
                <div key={t.key} className="space-y-4" data-testid={`partners-tier-${t.key}`}>
                  <h3 className="text-2xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">{t.label}</h3>
                  <div className="w-full rounded-xl overflow-hidden">
                    <img
                      src={t.url!}
                      alt={t.label}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
              ))}

              {/* Pre-CMS fallback: still rendering until at least one tier has an image */}
              {(!partnersTiers || (!partnersTiers.strategic && !partnersTiers.aligned && !partnersTiers.awareness)) && (
                <div className="w-full rounded-xl overflow-hidden">
                  <Image
                    src="/ourpartners.jpg"
                    alt="Our Partners"
                    width={1200}
                    height={600}
                    className="w-full h-auto object-contain"
                  />
                </div>
              )}
            </div>

            <GreenCTASection title={at.partners.collectiveImpact.title}>
              <Link
                href="/our-work"
                className="px-24 py-2.5 border border-white rounded-full text-sm font-semibold tracking-widest uppercase text-white hover:bg-white hover:text-primary transition"
              >
                {at.partners.collectiveImpact.learnMore}
              </Link>
            </GreenCTASection>

            {/* Tab navigation */}
            <div>
              <div className="border-t border-border mb-8" />
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setActiveTab('about')}
                  className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-primary hover:opacity-70 transition"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {at.partners.navLeft}
                </button>
                <button
                  onClick={() => setActiveTab('team')}
                  className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-primary hover:opacity-70 transition"
                >
                  {at.partners.navRight}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Our Team Tab */}
      {activeTab === 'team' && (
        <div className="mt-6 space-y-4">
          <div className="bg-white border border-border rounded-xl p-4 md:p-20 space-y-10">
            {/* Header */}
            <div className="flex flex-col items-center text-center space-y-4">
              <span className="text-sm font-semibold tracking-widest uppercase" style={{ color: '#9a8a50' }}>
                {at.team.label}
              </span>
              <p className="text-lg text-primary leading-relaxed max-w-2xl">{at.team.body}</p>
            </div>

            {/* Our Staff */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold text-primary text-center font-[family-name:var(--font-abhaya-libre)]">{at.team.ourStaff}</h2>
              {staffMembers.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {staffMembers.map((member) => (
                    <TeamBioCard
                      key={member.documentId}
                      member={member}
                      readBioLabel={at.team.readBio}
                      isAdmin={isAdmin}
                      onView={() => setViewBio(member)}
                      onEdit={() => { setEditBio(member); setShowTeamBioModal(true); }}
                      onDelete={() => setDeleteBio(member)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Our Board */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold text-primary text-center font-[family-name:var(--font-abhaya-libre)]">{at.team.ourBoard}</h2>
              {boardMembers.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {boardMembers.map((member) => (
                    <TeamBioCard
                      key={member.documentId}
                      member={member}
                      readBioLabel={at.team.readBio}
                      isAdmin={isAdmin}
                      onView={() => setViewBio(member)}
                      onEdit={() => { setEditBio(member); setShowTeamBioModal(true); }}
                      onDelete={() => setDeleteBio(member)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Tab navigation */}
            <div>
              <div className="border-t border-border mb-8" />
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setActiveTab('partners')}
                  className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-primary hover:opacity-70 transition"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {at.team.navLeft}
                </button>
                <button
                  onClick={() => setActiveTab('about')}
                  className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-primary hover:opacity-70 transition"
                >
                  {at.team.navRight}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bio view modal */}
      {viewBio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setViewBio(null)} />
          <div className="relative bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setViewBio(null)}
              className="absolute top-3 right-3 p-1 hover:bg-sidebar-hover rounded transition z-10"
            >
              <X size={20} className="text-foreground-muted" />
            </button>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                {viewBio.photo?.url ? (
                  <img
                    src={getStrapiImageUrl(viewBio.photo.url) ?? ''}
                    alt={viewBio.name}
                    className="w-16 h-16 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-background-secondary flex items-center justify-center shrink-0">
                    <span className="text-xl font-medium text-foreground-muted">{viewBio.name[0]}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-primary text-lg">{viewBio.name}</p>
                  <p className="text-sm text-foreground-muted">{viewBio.title}</p>
                </div>
              </div>
              <div className="border-t border-border" />
              <p className="text-base text-primary leading-relaxed">{viewBio.bio}</p>
            </div>
          </div>
        </div>
      )}

      {/* Edit team bio modal */}
      <CreateTeamBioModal
        isOpen={showTeamBioModal}
        onClose={() => { setShowTeamBioModal(false); setEditBio(null); }}
        editTeamBio={editBio}
        onResult={() => { setShowTeamBioModal(false); setEditBio(null); fetchTeamBios(); }}
      />

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!deleteBio}
        onClose={() => setDeleteBio(null)}
        title="Delete team member"
        subtitle={`Are you sure you want to delete "${deleteBio?.name}"? This action cannot be undone.`}
        footer={
          <>
            <button
              onClick={() => setDeleteBio(null)}
              className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-background-secondary transition"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteMember}
              className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition"
            >
              Delete
            </button>
          </>
        }
      >
        <></>
      </Modal>
    </div>
  );
}
